"use client";

import { c as _c } from "react/compiler-runtime";
import { Carousel, type CarouselApi, CarouselContent, CarouselItem } from "@repo/design-system/components/ui/carousel";
import { CustomImage } from "@repo/design-system/components/ui/custom-image";
import { CustomLink } from "@repo/design-system/components/ui/custom-link";
import { cn } from "@repo/design-system/utils/cn";
import Autoplay from "embla-carousel-autoplay";
import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "~/translations/loader/use-language";
import type { Job } from "~/types/job";
interface ApplicationProcessSlideProps {
  image: string;
  linkText?: string;
  linkUrl?: string;
  step: string;
  text: string;
}
function ApplicationProcessSlide(t0) {
  const $ = _c(17);
  const {
    step,
    text,
    image,
    linkText,
    linkUrl
  } = t0;
  let t1;
  if ($[0] !== image || $[1] !== step) {
    t1 = <div className="relative h-full min-h-[200px] w-full"><CustomImage alt={step} className="object-cover object-top" fill={true} sizes="(min-width: 1280px) 768px, 100vw" src={image} /></div>;
    $[0] = image;
    $[1] = step;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== step) {
    t2 = <h3 className="mb-4 font-bold text-3xl text-primary lg:text-3xl">{step}</h3>;
    $[3] = step;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== text) {
    t3 = <div className="font-light text-foreground sm:text-lg [&_p]:mb-2" dangerouslySetInnerHTML={{
      __html: text
    }} />;
    $[5] = text;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== linkText || $[8] !== linkUrl) {
    t4 = linkText && linkUrl && <CustomLink blank={true} className="mt-6 inline-block rounded-[8px] bg-primary px-8 py-3 text-center font-bold text-base text-white transition-colors duration-300 hover:brightness-90 lg:text-lg" href={linkUrl}>{linkText}</CustomLink>;
    $[7] = linkText;
    $[8] = linkUrl;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <div className="bg-white text-center"><div className="flex h-full min-h-[315px] w-full flex-col p-6 text-start sm:min-h-[260px] lg:p-10">{t2}{t3}{t4}</div></div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t1 || $[15] !== t5) {
    t6 = <div className="grid h-full w-full auto-cols-fr grid-flow-row grid-rows-[1fr,3fr] xs:grid-rows-[1fr,2fr] sm:grid-rows-[2fr,3fr] md:grid-rows-[1fr,1fr] lg:grid-flow-col lg:grid-rows-1 xl:min-h-[356px] xl:grid-cols-[3fr_2fr]">{t1}{t5}</div>;
    $[14] = t1;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
interface ApplicationProcessEconProps {
  job: Job;
}

// Job IDs that should hide the application process section
const HIDE_APPLICATION_IDS = ["228996", "229114", "229591", "229043", "230600"];
export default function ApplicationProcessEcon(t0) {
  const $ = _c(41);
  const {
    job
  } = t0;
  const t = useLanguage();
  const [api, setApi] = useState();
  const [currentIndex, setCurrentIndex] = useState(0);
  let t1;
  if ($[0] !== t.econ_applicationProcess_step1 || $[1] !== t.econ_applicationProcess_text1) {
    t1 = {
      step: t.econ_applicationProcess_step1,
      text: t.econ_applicationProcess_text1,
      image: "images/jobad/econ/applicationprocess/step1.webp"
    };
    $[0] = t.econ_applicationProcess_step1;
    $[1] = t.econ_applicationProcess_text1;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t.econ_applicationProcess_step2 || $[4] !== t.econ_applicationProcess_text2) {
    t2 = {
      step: t.econ_applicationProcess_step2,
      text: t.econ_applicationProcess_text2,
      image: "images/jobad/econ/applicationprocess/step2.webp"
    };
    $[3] = t.econ_applicationProcess_step2;
    $[4] = t.econ_applicationProcess_text2;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t.econ_applicationProcess_step3 || $[7] !== t.econ_applicationProcess_text3) {
    t3 = {
      step: t.econ_applicationProcess_step3,
      text: t.econ_applicationProcess_text3,
      image: "images/jobad/econ/applicationprocess/step3.webp"
    };
    $[6] = t.econ_applicationProcess_step3;
    $[7] = t.econ_applicationProcess_text3;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t.econ_applicationProcess_step4 || $[10] !== t.econ_applicationProcess_step4_linkText || $[11] !== t.econ_applicationProcess_text4 || $[12] !== t.econ_applicationProcess_tips) {
    t4 = {
      step: t.econ_applicationProcess_step4,
      text: t.econ_applicationProcess_text4,
      linkText: t.econ_applicationProcess_step4_linkText,
      linkUrl: t.econ_applicationProcess_tips,
      image: "images/jobad/econ/applicationprocess/step4.webp"
    };
    $[9] = t.econ_applicationProcess_step4;
    $[10] = t.econ_applicationProcess_step4_linkText;
    $[11] = t.econ_applicationProcess_text4;
    $[12] = t.econ_applicationProcess_tips;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = [t1, t2, t3, t4];
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const steps = t5;
  let t6;
  let t7;
  if ($[19] !== api) {
    t6 = () => {
      if (!api) {
        return;
      }
      const updateIndex = () => setCurrentIndex(api.selectedScrollSnap());
      updateIndex();
      api.on("select", updateIndex);
      return () => {
        api.off("select", updateIndex);
      };
    };
    t7 = [api];
    $[19] = api;
    $[20] = t6;
    $[21] = t7;
  } else {
    t6 = $[20];
    t7 = $[21];
  }
  useEffect(t6, t7);
  if (HIDE_APPLICATION_IDS.includes(job.data.idClient)) {
    return null;
  }
  let t8;
  if ($[22] !== t.econ_applicationProcess_HL) {
    t8 = <h2 className="mb-10 text-center font-bold text-4xl text-white xl:text-5xl">{t.econ_applicationProcess_HL}</h2>;
    $[22] = t.econ_applicationProcess_HL;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t10;
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      loop: true,
      watchDrag: false
    };
    t10 = Autoplay({
      delay: 10000,
      stopOnMouseEnter: true
    });
    $[24] = t10;
    $[25] = t9;
  } else {
    t10 = $[24];
    t9 = $[25];
  }
  let t11;
  if ($[26] !== t10) {
    t11 = [t10];
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== steps) {
    t12 = <CarouselContent className="ml-0">{steps.map(_temp)}</CarouselContent>;
    $[28] = steps;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t11 || $[31] !== t12) {
    t13 = <Carousel className="w-full" opts={t9} plugins={t11} setApi={setApi}>{t12}</Carousel>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== api || $[34] !== currentIndex || $[35] !== steps) {
    t14 = <div className="mx-auto mt-5 flex h-full w-full justify-center">{steps.map((_, index_0) => <button aria-label={`Go to step ${index_0 + 1}`} className={cn("mx-2 h-3 w-10 cursor-pointer bg-white transition-opacity", index_0 === currentIndex ? "opacity-100" : "opacity-20")} key={index_0} onClick={() => api?.scrollTo(index_0)} type="button" />)}</div>;
    $[33] = api;
    $[34] = currentIndex;
    $[35] = steps;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t13 || $[38] !== t14 || $[39] !== t8) {
    t15 = <section className="outer-container bg-linear-to-tr from-[#ea1b0a] to-[#00375c] py-10 md:py-20 print:hidden" id="applicationProcess"><div className="inner-container grid h-full justify-center">{t8}{t13}{t14}</div></section>;
    $[37] = t13;
    $[38] = t14;
    $[39] = t8;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
}
function _temp(step, index) {
  return <CarouselItem className="pl-0" key={index}><ApplicationProcessSlide {...step} /></CarouselItem>;
}